(function () {
  if (window.customElements.get('infinite-scroll')) {
    return;
  }

  function rafThrottle(callback) {
    let requestId = null;

    return function () {
      if (requestId !== null) return;

      requestId = requestAnimationFrame(function () {
        requestId = null;
        callback();
      });
    };
  }

  class InfiniteScroll extends HTMLElement {
    #loading = false;
    #finished = false;
    #observer = null;
    #abortController = null;
    #pageAnchors = [];
    #currentPage = 1;
    #productList = null;

    connectedCallback() {
      this.#productList =
        document.getElementById('product-list-' + this.getAttribute('section-id')) ||
        this.closest('.collection__main')?.querySelector('product-list');

      if (!this.#productList) return;

      this.#currentPage = parseInt(this.getAttribute('current-page'), 10) || 1;

      // A page rendered at ?page=N only contains page N's products - the earlier pages that were
      // scrolled through before a reload are gone and there's nothing above to scroll back to,
      // which leaves the visitor stuck mid-list. Restart from page 1 instead.
      if (this.#currentPage > 1 && !window.Shopify?.designMode) {
        const url = new URL(window.location.href);
        url.searchParams.delete('page');
        window.location.replace(url.toString());
        return;
      }

      this.classList.add('infinite-scroll--enabled');

      // Server-rendered products ko unke page number se tag karo
      const initialItems = this.#itemsOf(this.#productList);
      initialItems.forEach((item) => (item.dataset.page = this.#currentPage));

      if (initialItems.length > 0) {
        this.#pageAnchors.push({ page: this.#currentPage, element: initialItems[0] });
      }

      this.#abortController = new AbortController();

      window.addEventListener('scroll', rafThrottle(this.#syncUrl.bind(this)), {
        passive: true,
        signal: this.#abortController.signal
      });

      // #syncUrl rewrites the address bar to ?page=N while scrolling. A reload at that URL (such
      // as the footer country/language switch, which submits a Shopify localization form) only
      // renders page N's products, without the earlier pages, so send it back to page 1 instead.
      document.addEventListener(
        'submit',
        (event) => {
          const form = event.target;

          if (!(form instanceof HTMLFormElement) || !form.action.includes('/localization')) return;

          let returnTo = form.elements['return_to'];

          if (!returnTo) {
            returnTo = document.createElement('input');
            returnTo.type = 'hidden';
            returnTo.name = 'return_to';
            form.appendChild(returnTo);
          }

          const url = new URL(window.location.href);
          url.searchParams.delete('page');
          returnTo.value = url.pathname + url.search;

          // Shopify can also fall back to the request's Referer instead of return_to, which is
          // the browser's current URL - so drop ?page there too before the form is sent.
          history.replaceState(history.state, '', url.toString());
        },
        { signal: this.#abortController.signal }
      );

      this.#observer = new IntersectionObserver(this.#onIntersect.bind(this), {
        rootMargin: '800px 0px'
      });

      this.#observer.observe(this);
    }

    disconnectedCallback() {
      this.#observer?.disconnect();
      this.#abortController?.abort();
    }

    get nextUrl() {
      return this.querySelector('a[rel="next"]')?.href || null;
    }

    #itemsOf(root) {
      return Array.from(root.children).filter((element) =>
        element.matches('product-card, .product-list__promo')
      );
    }

    #onIntersect(entries) {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.#loadNext();
      }
    }

    async #loadNext() {
      if (this.#loading || this.#finished) return;

      const nextUrl = this.nextUrl;

      if (!nextUrl) {
        this.#finished = true;
        this.#observer?.disconnect();
        return;
      }

      this.#loading = true;
      this.setAttribute('aria-busy', 'true');

      const requestUrl = new URL(nextUrl, window.location.origin);
      const page = parseInt(requestUrl.searchParams.get('page'), 10) || 1;
      requestUrl.searchParams.set('section_id', this.getAttribute('section-id'));

      try {
        const response = await fetch(requestUrl.toString());

        if (!response.ok) throw new Error(response.statusText);

        const parsedDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
        const newProductList = parsedDocument.querySelector('product-list');
        const newPagination = parsedDocument.querySelector('.pagination');

        if (newProductList) {
          const fragment = document.createDocumentFragment();
          let firstItem = null;

          this.#itemsOf(newProductList).forEach((item) => {
            const node = document.importNode(item, true);

            node.removeAttribute('reveal-on-scroll');
            node.classList.add('infinite-scroll__item');
            node.dataset.page = page;

            firstItem = firstItem || node;
            fragment.appendChild(node);
          });

          this.#productList.appendChild(fragment);

          if (firstItem) {
            this.#pageAnchors.push({ page: page, element: firstItem });
          }
        }

        const currentPagination = this.querySelector('.pagination');

        if (newPagination) {
          const importedPagination = document.importNode(newPagination, true);

          currentPagination
            ? currentPagination.replaceWith(importedPagination)
            : this.prepend(importedPagination);
        } else {
          currentPagination?.remove();
          this.#finished = true;
          this.#observer?.disconnect();
        }

        this.#syncUrl();

        // Agar element abhi bhi viewport ke qareeb hai to agla page bhi load karo
        if (!this.#finished) {
          this.#observer.unobserve(this);
          this.#observer.observe(this);
        }
      } catch (error) {
        // Fail hone par pagination wapis dikha do taake user manually navigate kar sake
        this.classList.remove('infinite-scroll--enabled');
        this.#observer?.disconnect();
        this.#finished = true;
      } finally {
        this.#loading = false;
        this.removeAttribute('aria-busy');
      }
    }

    #syncUrl() {
      if (this.#pageAnchors.length === 0) return;

      const threshold = window.innerHeight * 0.35;
      let page = this.#pageAnchors[0].page;

      for (const anchor of this.#pageAnchors) {
        if (anchor.element.getBoundingClientRect().top <= threshold) {
          page = anchor.page;
        } else {
          break;
        }
      }

      if (page === this.#currentPage) return;

      this.#currentPage = page;

      const url = new URL(window.location.href);

      page > 1 ? url.searchParams.set('page', page) : url.searchParams.delete('page');

      history.replaceState(history.state, '', url.toString());
    }
  }

  window.customElements.define('infinite-scroll', InfiniteScroll);
})();